import { create } from "zustand"
import { persist } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"
import { z } from "zod"
import type { SettingsState } from "@/src/common/settings/types"
import type { ThemeDirection, ThemeColorScheme } from "@/src/theme/types"
import type { SettingsStore } from "../types"
import { defaultSettings, STORAGE_KEY } from "@/src/common/settings/config-settings"

// Zod schemas for validation
const ThemeDirectionSchema = z.enum(["ltr", "rtl"])
const ThemeColorSchemeSchema = z.enum(["light", "dark"])
const ContrastSchema = z.enum(["default", "hight"])
const NavColorSchema = z.enum(["integrate", "apparent"])
const NavLayoutSchema = z.enum(["vertical", "horizontal", "mini"])
const PrimaryColorSchema = z.enum(["default", "cyan", "purple", "blue", "orange", "red"])

const SettingsStateSchema = z.object({
    fontFamily: z.string(),
    compactLayout: z.boolean(),
    direction: ThemeDirectionSchema,
    colorScheme: ThemeColorSchemeSchema,
    contrast: ContrastSchema,
    navColor: NavColorSchema,
    navLayout: NavLayoutSchema,
    primaryColor: PrimaryColorSchema,
})

export const useSettingsStore = create<SettingsStore>()(
    persist(
        immer((set, get) => ({
                // State
                settings: defaultSettings,
                openDrawer: false,
                canReset: false,
                loading: false,
                error: null,

                // Actions
                    onUpdate: (updateValue: Partial<SettingsState>) => {
                        set((state) => {
                            state.settings = { ...state.settings, ...updateValue }
                            state.canReset = JSON.stringify(state.settings) !== JSON.stringify(defaultSettings)
                            state.error = null
                        })
                    },

                    onUpdateField: (name: keyof SettingsState, value: any) => {
                        set((state) => {
                            state.settings[name] = value
                            state.canReset = JSON.stringify(state.settings) !== JSON.stringify(defaultSettings)
                            state.error = null
                        })
                    },

                onReset: () => {
                    set((state) => {
                        state.settings = { ...defaultSettings }
                        state.canReset = false
                        state.error = null
                    })
                },

                onToggleDrawer: () => {
                    set((state) => {
                        state.openDrawer = !state.openDrawer
                    })
                },

                onCloseDrawer: () => {
                    set((state) => {
                        state.openDrawer = false
                    })
                },

                })),
                {
                    name: STORAGE_KEY,
                    partialize: (state) => ({
                        settings: state.settings,
                        canReset: state.canReset,
                    }),
                    onRehydrateStorage: () => (state) => {
                        if (state) {
                            // Calculate canReset after rehydration
                            state.canReset = JSON.stringify(state.settings) !== JSON.stringify(defaultSettings)
                        }
                    },
                }
            )
    )
